import {
  addExpandedNode,
  pinNode,
  removeExpandedNode,
  removeTrackExpandedNodes,
  trackExpandedNodes,
  unpinNode,
  updateExpandedNodes,
} from "../../redux/slices/graphSlice";
import { toast } from "react-toastify";
import { MdPushPin } from "react-icons/md";
import { BsFolderSymlink, BsPin } from "react-icons/bs";
import {
  FaGetPocket,
  FaDownload,
  FaEye,
  FaExpandAlt,
  FaCamera,
} from "react-icons/fa";
import { PiSelectionFill, PiSelectionDuotone } from "react-icons/pi";
import { RiCollapseDiagonalFill } from "react-icons/ri";
import ReactDOMServer from "react-dom/server";

const NodeContextMenu = ({
  cy,
  updateNodeLabels,
  handleSelectClick,
  handleDeselectClick,
  setShowDraftModal,
  setIsInfoModal,
  setAttachments,
  generateNodeLink,
  fetchedData,
  nodes,
  dispatch,
  isSuperAdmin,
  pinnedNodes,
  getAllNodes,
  setLinkUrl,
  setLinkNodeName,
  toggleLinkModal,
  setMessage,
  expandedNodes: fetchedExpandedNodes,
  expandedNodesMap,
  fetchedNodesData,
  draftedNodes,
}) => {
  const defaultLayout = {
    randomize: false,
    name: "cola",
    animate: true,
    refresh: 2,
    maxSimulationTime: 100,
    fit: true,
    handleDisconnected: true,
    infinite: false,
    ungrabifyWhileSimulating: false,
    nodeDimensionsIncludeLabels: true,
    convergenceThreshold: 0.1,
    nodeSpacing: function (node) {
      return 30;
    },
    ready: () => {},
    stop: () => {},
    unconstrIter: 10,
    userConstIter: 10,
    allConstIter: 0,
    gridifyIter: 5,
    edgeLength: (edge) => {
      const nodes = edge.connectedNodes();
      const degs = nodes.minDegree();

      // Base length based on node degree
      let baseLength = 100 + 65 * Math.sqrt(degs - 1);

      // Get the label and measure its length
      const edgeData = edge.data();
      const label = edgeData.label || "";
      const labelLength = label.length;

      // Calculate edge length dynamically based on label length
      const lengthAdjustment = labelLength * 5; // Adjust multiplier as needed for spacing
      return baseLength + lengthAdjustment;
    },
  };

  // Function to create context menu commands
  const mkCtxCommand = (command, name, icon, enabled) => {
    return {
      content: `<div style="font-size: 12px; display: flex; justify-content: center; align-items: center; text-align: center; flex-direction: column;">
                <span style="margin-bottom: 5px;">${icon}</span> 
                ${name}
              </div>`,
      select: function (ele, event) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        // Wrap command execution in setTimeout to break event chain
        setTimeout(() => {
          command(event);
        }, 0);
      },
      disabled: !enabled,
      fillColor: "rgba(0,0,0," + (enabled ? "1" : "0.6") + ")",
    };
  };

  const setupContextMenu = () => {
    cy.container().addEventListener(
      "contextmenu",
      (e) => {
        e.preventDefault();
        return false;
      },
      false
    );

    cy.on("cxttapstart", (event) => {
      const originalEvent = event.originalEvent;
      if (originalEvent) {
        originalEvent.preventDefault();
        originalEvent.stopPropagation();
      }
    });

    cy.cxtmenu({
      selector: "node",
      activePadding: 20,
      menuRadius: function () {
        const baseRadius = 100; // Base radius when zoom is 1
        if (cy.zoom() > 1) {
          if (cy.zoom() > 1.5 && cy.zoom() < 3) return 80;
          if (cy.zoom() >= 3 && cy.zoom() < 10) return 60;
          if (cy.zoom() > 20) return 10;
          return baseRadius / cy.zoom();
        } else return baseRadius;
      },
      zoomDuration: 0,
      commands: (ele) => {
        let cmds = [];
        const nodeId = ele.id();
        const isPinned = pinnedNodes.has(nodeId);
        const isExpanded = fetchedExpandedNodes.has(parseInt(nodeId));
        const pushPinIcon = ReactDOMServer.renderToString(
          <MdPushPin size={13} />
        );
        const pushUnpinIcon = ReactDOMServer.renderToString(
          <BsPin size={13} />
        );
        const pushLinkIcon = ReactDOMServer.renderToString(
          <BsFolderSymlink size={13} />
        );
        const pushGetkIcon = ReactDOMServer.renderToString(
          <FaGetPocket size={13} />
        );
        const pushDraftIcon = ReactDOMServer.renderToString(
          <FaDownload size={13} />
        );
        const pushHighlightIcon = ReactDOMServer.renderToString(
          <FaEye size={13} />
        );
        const pushExpandIcon = ReactDOMServer.renderToString(
          <FaExpandAlt size={13} />
        );
        const pushSnapIcon = ReactDOMServer.renderToString(
          <FaCamera size={13} />
        );
        const pushSelectIcon = ReactDOMServer.renderToString(
          <PiSelectionFill size={13} />
        );
        const pushDeselectIcon = ReactDOMServer.renderToString(
          <PiSelectionDuotone size={13} />
        );
        const pushCollapseIcon = ReactDOMServer.renderToString(
          <RiCollapseDiagonalFill size={13} />
        );

        // Pin/Unpin command
        if (!isPinned) {
          cmds.push(
            mkCtxCommand(
              () => {
                // const currentPosition = ele.position();
                // ele.style({
                //   "border-width": 4,
                //   "border-color": "red",
                // });
                // ele.position(currentPosition);
                dispatch(pinNode(nodeId));
                // cy.cxtmenu().show(ele);
              },
              "Pin",
              pushPinIcon,
              true
            )
          );
        } else {
          cmds.push(
            mkCtxCommand(
              () => {
                // ele.style({
                //   "border-width": 0,
                // });

                dispatch(unpinNode(nodeId));
              },
              "Unpin",
              pushUnpinIcon,
              true
            )
          );
        }

        // Expand the node command
        if (!isExpanded) {
          const nodeId = ele.data("id");
          const allNodes = fetchedData ? fetchedData.nodes : [];
          const allEdges = fetchedData ? fetchedData.edges : [];

          // Check if node label includes edge count (e.g., "Records of Documentation(:PlaybookRule)[2]")
          const nodeLabel = ele.data("label");
          const edgeCountMatch = nodeLabel && nodeLabel.match(/\[\d+\]$/); // Regex to check if label ends with [number]

          const hasFurtherNodes = allEdges.some(
            (edge) =>
              edge.source === parseInt(nodeId) ||
              edge.target === parseInt(nodeId)
          );

          cmds.push(
            mkCtxCommand(
              async () => {
                if (fetchedData && hasFurtherNodes) {
                  let clickedNode = allNodes.find(
                    (node) => node.id === parseInt(nodeId)
                  );

                  if (!clickedNode) {
                    console.error(
                      "Node with ID",
                      nodeId,
                      "is not found in fetchedData."
                    );
                    return;
                  }

                  const nodeOId = clickedNode.props
                    ? clickedNode.props._oid
                    : null;

                  if (!nodeOId) {
                    console.error(
                      "Node ID (_oid) is not found in the element data."
                    );
                    return;
                  }

                  try {
                    const childEdges = allEdges.filter(
                      (edge) =>
                        edge.source === parseInt(nodeId) &&
                        allNodes.some((node) => node.id === edge.target) // Ensure target node exists
                    );

                    const childNodes = childEdges
                      .map((edge) => {
                        const targetNode = allNodes.find(
                          (node) => node.id === edge.target
                        );
                        if (!targetNode) {
                          console.warn(
                            `Target node ${edge.target} not found for edge ${edge.id}`
                          );
                          return null;
                        }
                        return {
                          data: {
                            id: targetNode.id,
                            label: targetNode.display,
                            color: targetNode.colour || "#666",
                          },
                        };
                      })
                      .filter((node) => node !== null);

                    const incomingEdges = allEdges.filter(
                      (edge) =>
                        edge.target === parseInt(nodeId) &&
                        allNodes.some((node) => node.id === edge.source) // Ensure source node exists
                    );

                    const sourceNodes = incomingEdges
                      .map((edge) => {
                        const sourceNode = allNodes.find(
                          (node) => node.id === edge.source
                        );
                        return sourceNode
                          ? {
                              data: {
                                id: sourceNode.id,
                                label: sourceNode.display,
                                color: sourceNode.colour || "#666",
                              },
                            }
                          : null;
                      })
                      .filter((node) => node !== null);

                    const newEdges = [...childEdges, ...incomingEdges].map(
                      (edge) => ({
                        data: {
                          id: edge.id,
                          source: edge.source,
                          target: edge.target,
                          label:
                            edge.class === "IsAnswerFor" && edge.props.value
                              ? `${edge.display} = ${edge.props.value}`
                              : edge.display || "",
                        },
                      })
                    );

                    const newElements = [
                      ...childNodes,
                      ...sourceNodes,
                      ...newEdges,
                    ];
                    const existingIds = new Set(
                      cy.nodes().map((node) => node.id())
                    );

                    const expandedNodes = [
                      ...childNodes,
                      ...sourceNodes,
                      {
                        data: {
                          id: clickedNode.id,
                          label: clickedNode.display,
                          color: clickedNode.colour || "#666",
                        },
                      },
                    ].filter((node) => !existingIds.has(String(node.data.id)));

                    const uniqueNewNodes = newElements.filter(
                      (elem) =>
                        elem.data &&
                        elem.data.id &&
                        !existingIds.has(String(elem.data.id))
                    );

                    if (uniqueNewNodes.length > 0) {
                      cy.add(uniqueNewNodes);
                      cy?.layout(defaultLayout).run();

                      const updatedExpandedNodes = expandedNodes.map(
                        (node) => ({
                          ...node, // Shallow copy of the node object
                          data: {
                            ...node.data, // Shallow copy of the data object
                            label: node.data.label
                              ? node.data.label
                              : "Default Label", // You can update the label here
                          },
                        })
                      );
                      dispatch(
                        trackExpandedNodes({
                          nodeId: clickedNode.id,
                          updatedExpandedNodes,
                        })
                      );

                      expandedNodes.forEach((node) => {
                        dispatch(addExpandedNode(node.data.id));
                      });

                      dispatch(
                        updateExpandedNodes({
                          nodeId: clickedNode.id,
                          expanded: true,
                        })
                      );
                    } else {
                      console.warn("No new unique elements to add.");
                    }

                    updateNodeLabels();
                    ele.style({
                      "border-width": 5,
                      "border-color": "blue",
                      "border-style": "double",
                      "text-outline-width": 2,
                    });
                  } catch (error) {
                    console.error("Error while expanding node:", error);
                  }
                } else if (!hasFurtherNodes) {
                  console.warn(
                    "Expand command is disabled. This node has no further nodes to expand."
                  );
                }
              },
              "Expand",
              pushExpandIcon,
              fetchedData !== null && hasFurtherNodes && edgeCountMatch
            )
          );
        }

        // Collapse the node command
        if (isExpanded) {
          cmds.push(
            mkCtxCommand(
              async () => {
                try {
                  // Helper function to recursively get all expanded children and their edges
                  const collectNodesToCollapse = (
                    node,
                    collected = new Set()
                  ) => {
                    const expandedNodes =
                      expandedNodesMap[node.id()] ||
                      // Array.from(allExpandedNodes).map((nodeId) => {
                      //   return {
                      //     data: {
                      //       id: nodeId,
                      //     },
                      //   };
                      // }) ||
                      [];
                    for (const expandedNode of expandedNodes) {
                      const childNode = cy.getElementById(
                        String(expandedNode.data?.id)
                      );
                      if (
                        childNode.length > 0 &&
                        !collected.has(childNode?.id())
                      ) {
                        collected.add(childNode?.id());
                        if (
                          fetchedExpandedNodes.has(parseInt(childNode?.id()))
                        ) {
                          collectNodesToCollapse(childNode, collected);
                        }
                      }
                    }

                    return collected;
                  };

                  // Collect all nodes that need to be collapsed
                  const nodesToCollapseIds = collectNodesToCollapse(ele);

                  // Create collections for nodes and edges to remove
                  const nodesToCollapse = cy.collection();
                  const edgesToRemove = cy.collection();

                  // Add nodes and their connected edges to respective collections
                  nodesToCollapseIds.forEach((nodeId) => {
                    const node = cy.getElementById(nodeId);
                    if (node.length > 0) {
                      nodesToCollapse.merge(node);

                      // Get all edges connected to this node
                      const connectedEdges = node.connectedEdges();
                      edgesToRemove.merge(connectedEdges);
                    }
                  });

                  // Perform collapse operation in a batch
                  cy?.batch(() => {
                    // Remove edges first
                    edgesToRemove.remove();

                    // Remove nodes
                    nodesToCollapse.remove();

                    // Update the current node's state and styling
                    dispatch(removeTrackExpandedNodes(ele.id()));
                  });

                  // Update Redux state for all collapsed nodes
                  nodesToCollapse.forEach((node) => {
                    dispatch(removeExpandedNode(node.id()));

                    // Also update expanded state in Redux for each collapsed node
                    dispatch(
                      updateExpandedNodes({
                        nodeId: node.id(),
                        expanded: false,
                      })
                    );
                  });

                  updateNodeLabels();

                  // Update Redux state for the clicked node
                  dispatch(
                    updateExpandedNodes({
                      nodeId: ele.id(),
                      expanded: false,
                    })
                  );

                  console.log(
                    `Node ${ele.id()} and all its nested children collapsed successfully. 
                     Total nodes collapsed: ${nodesToCollapse.length}`
                  );
                } catch (error) {
                  console.error("Failed to collapse nodes:", error);
                }
              },
              "Collapse",
              pushCollapseIcon,
              isExpanded
            )
          );
        }

        // Save graph snapshot
        cmds.push(
          mkCtxCommand(
            () => {
              const backgroundColor = "#FFFFFF";
              const pngData = cy.png({
                full: true,
                scale: 2,
                bg: backgroundColor,
              });
              const link = document.createElement("a");
              link.href = pngData;
              link.download = "graph-snapshot.png";
              link.click();
            },
            "Snapshot",
            pushSnapIcon,
            true
          )
        );

        // Get All Nodes command
        cmds.push(
          mkCtxCommand(
            async () => {
              await getAllNodes(nodeId);
            },
            "Get All Nodes",
            pushGetkIcon,
            // !hasFetchedChildNodes
            true
          )
        );

        // Link to node
        cmds.push(
          mkCtxCommand(
            (event) => {
              const nodeId = ele.id();
              const nodeName = ele.data("label");
              const data = nodes.find((node) => node.id === parseInt(nodeId));
              if (data) {
                const url = generateNodeLink(data.props._oid, data);
                setLinkUrl(url);
                setLinkNodeName(data.props.name);
                setTimeout(() => {
                  toggleLinkModal();
                }, 0);
              } else {
                console.error("Node data not found for node ID:", nodeId);
              }
            },
            "Link",
            pushLinkIcon,
            true
          )
        );

        // Select command
        cmds.push(
          mkCtxCommand(
            async () => {
              const nodeId = ele.data("id");
              let clickedNode = fetchedData
                ? fetchedData.nodes.find((node) => node.id === parseInt(nodeId))
                : null;

              if (!clickedNode) {
                clickedNode = nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }
              const nodeOId = clickedNode ? clickedNode.props._oid : null;

              if (!nodeOId) {
                console.error(
                  "Node ID (_oid) is not found in the element data."
                );
                return;
              }

              handleSelectClick(nodeOId, clickedNode);
            },
            "Select",
            pushSelectIcon,
            // currentRole !== "viewer"
            isSuperAdmin
          )
        );

        // Deselect command
        cmds.push(
          mkCtxCommand(
            () => {
              const nodeId = ele.data("id");
              let clickedNode = fetchedData
                ? fetchedData.nodes.find((node) => node.id === parseInt(nodeId))
                : null;

              if (!clickedNode) {
                clickedNode = nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }
              const nodeOId = clickedNode ? clickedNode.props._oid : null;

              if (!nodeOId) {
                console.error(
                  "Node ID (_oid) is not found in the element data."
                );
                return;
              }

              handleDeselectClick(nodeOId);
            },
            "Deselect",
            pushDeselectIcon,
            // currentRole !== "viewer"
            isSuperAdmin
          )
        );

        // Highlight node
        cmds.push(
          mkCtxCommand(
            () => {
              cy.nodes().forEach((node) => {
                if (node.id() !== ele.id()) {
                  node.style({
                    opacity: 0.2,
                  });
                } else {
                  node.style({
                    opacity: 1,
                  });
                }
              });

              const connectedEdges = cy
                .edges()
                .filter(
                  (edge) =>
                    edge.source().id() === ele.id() ||
                    edge.target().id() === ele.id()
                );

              connectedEdges.forEach((edge) => {
                edge.style({
                  opacity: 1,
                });

                const sourceNode = edge.source();
                const targetNode = edge.target();

                sourceNode.style({
                  opacity: 1,
                });

                targetNode.style({
                  opacity: 1,
                });
              });

              cy.edges().forEach((edge) => {
                if (!connectedEdges.includes(edge)) {
                  edge.style({
                    opacity: 0.2,
                    "line-color": "#ccc",
                  });
                }
              });
            },
            "Highlight",
            pushHighlightIcon,
            true
          )
        );

        // Draft command
        cmds.push(
          mkCtxCommand(
            async () => {
              const nodeId = ele.data("id");
              // Check if the node has already been drafted
              if (draftedNodes.current.some((att) => att.nodeId === nodeId)) {
                toast.error("This node has already been drafted.");
                return; // Exit if the node is already drafted
              }

              let clickedNode = null;

              if (fetchedData && fetchedData.nodes) {
                clickedNode = fetchedData.nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }
              if (!clickedNode && fetchedNodesData && fetchedNodesData?.nodes) {
                clickedNode = fetchedNodesData.nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }

              if (!clickedNode && fetchedNodesData && fetchedNodesData?.nodes) {
                clickedNode = fetchedNodesData.nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }

              if (!clickedNode) {
                clickedNode = nodes.find(
                  (node) => node.id === parseInt(nodeId)
                );
              }

              if (clickedNode) {
                const nodeOId = clickedNode.props._oid;
                if (nodeOId) {
                  if (clickedNode.props.attachment) {
                    const fileExtension = clickedNode?.props?.attachment
                      ?.split(".")
                      .pop()
                      .toLowerCase();
                    let newAttachment = {};
                    if (fileExtension === "docx") {
                      newAttachment = {
                        nodeId: nodeId,
                        attachment: clickedNode.props.attachment || null,
                      };
                    } else {
                      setMessage(
                        "Drafting is only supported for 'docx' file type."
                      );
                    }

                    if (newAttachment.attachment) {
                      setAttachments((prev) => {
                        const exists = prev.some(
                          (att) =>
                            att.nodeId === newAttachment.nodeId &&
                            att.attachment === newAttachment.attachment
                        );

                        if (exists) {
                          // toast.error("This node has already been drafted.");
                          return prev; // Return the existing state without adding the duplicate
                        }

                        return [...prev, newAttachment]; // Add the new attachment if it's not a duplicate
                      });
                      setIsInfoModal(false);
                    } else {
                      setIsInfoModal(true);
                    }
                    setShowDraftModal(true);
                  } else {
                    toast.error("No Attachment found");
                  }
                } else {
                  console.error(
                    "Node ID (_oid) is not found in the element data."
                  );
                }
              } else {
                console.error("Clicked node not found.");
              }
            },
            "Draft",
            pushDraftIcon,
            true
          )
        );

        return cmds;
      },
      fillColor: "rgba(255, 255, 255, 0.8)",
      activeFillColor: "rgba(0, 0, 0, 0.6)",
      activeOpacity: 0.75,
      indicatorSize: 24,
      separatorWidth: 3,
      zIndex: 9999, // Ensure the context menu appears on top
      animationDuration: 0, // Disable context menu animation
      preventContextMenuForPopup: true, // Prevent context menu for popup elements
      preventContextMenuForLink: true, // Prevent context menu for links
    });
  };

  return { setupContextMenu };
};

export default NodeContextMenu;
